import { defineStore } from "pinia";
import {
    getBaseSettings,
    getBaseOrganizationSettings,
    getOverlayGeoJSON,
} from "@/utils/api";
// import { env } from "@/env.js";

export const defaultState = () => ({
    unitPreference: "Metric",
    timezone: "America/Vancouver",
    userTzAbbr: "PST",
    showRainScale: true,
    showGaugeScale: true,
    forecastLevels: [],
    gaugeLevels: [],
    loaded: false,
    email: "",
    alertByEmail: false,
    phoneNo: "",
    alertBySms: false,
    forecastNotificationSettings: [],
    gaugeNotificationSettings: {},
    isAdmin: false,
    adminMode: false,
    adminAction: "",
    adminSidebarCollapse: false,
    assetCount: 0,
    hasActiveSubscription: false,
    overlays: [],
    // Here by sub 12 hour forecast stuff
    subForecastNotificationSettings: {
        schedule: '04:00am/pm',
        escalationWarning: 'always',
        escalationLevel: 3,
    },
});

export const useSettingStore = defineStore("settings", {
    state: defaultState,
    getters: {
        validEmail() {
            if (this.email === null || this.email === undefined)
                return !this.alertByEmail;
            const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
            if (
                this.email.match(validRegex) ||
        (this.email === "" && !this.alertByEmail)
            ) {
                return true;
            } else {
                return false;
            }
        },
        validNumber() {
            if (this.phoneNo === null || this.phoneNo === undefined) return !this.alertBySms;
            const validRegex = /^1?\s*\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/;
            if (this.phoneNo.match(validRegex) || (this.phoneNo === "" && !this.alertBySms)) {
                return true;
            } else {
                return false;
            }
        },
        invalidNotificationContact() {
            return this.loaded && (this.phoneNo === '' || !this.alertBySms) && (this.email === '' || !this.alertByEmail)
        },
        validGaugeNotification() {
            if (this.gaugeNotificationSettings.allowNotifications) return true;
            if (this.gaugeNotificationSettings.startAlertWindow === null || this.gaugeNotificationSettings.endAlertWindow === null) return false;
            let gotOne = false;
            if (this.gaugeNotificationSettings.riskLevelData) {
                this.gaugeNotificationSettings.riskLevelData.forEach((level) => {
                    if (level.usesNotificationTimes) gotOne = true;
                });
            }
            return gotOne;
        },
        validForecastLevels() {
            let isValid = true;
            let lastYear = 0;
            const descriptions = [];
            const colors = [];
            this.forecastLevels.forEach((level) => {
                // Check for repeat descriptions
                if (descriptions.includes(level.riskLabel)) {
                    isValid = false;
                } else {
                    descriptions.push(level.riskLabel);
                }
                // Check for blank descriptions and long descriptions
                if (level.riskLabel.length < 1) isValid = false;
                // Check for repeat colors
                if (colors.includes(level.color)) {
                    isValid = false;
                } else {
                    colors.push(level.color);
                }
                // Check for ascending years
                if (level.lowerBound !== null) {
                    if (level.lowerBound <= lastYear) {
                        isValid = false;
                    }
                    lastYear = level.lowerBound;
                }
            });
            return isValid;
        },
        validGaugeThresholds() {
            let isValid = true;
            const descriptions = [];
            const colors = [];
            this.gaugeLevels.forEach((level) => {
                // Check for repeat descriptions
                if (descriptions.includes(level.riskLabel)) {
                    isValid = false;
                } else {
                    descriptions.push(level.riskLabel);
                }
                // Check for blank descriptions and long descriptions
                if (level.riskLabel.length < 1 || level.riskLabel.length > 20) isValid = false;
                // Check for repeat colors
                if (colors.includes(level.color)) {
                    isValid = false;
                } else {
                    colors.push(level.color);
                }
            });
            return isValid;
        },
        forecastReturnPeriodText() {
            return (riskLevel) => {
                const myLevel = this.forecastLevels.find(
                    (level) => level.riskLevel === riskLevel,
                );
                if (riskLevel === 1) {
                    return `Return Period < ${myLevel.upperBound} Years`;
                } else {
                    return `Return Period ≥ ${myLevel.lowerBound} Years`;
                }
            };
        },
        gaugeReturnPeriodText() {
            return (riskLevel) => {
                const myLevel = this.gaugeLevels.find(
                    (level) => level.riskLevel === riskLevel,
                );
                if (riskLevel === 1) {
                    return `Return Period < ${myLevel.upperBound} Years`;
                } else {
                    return `Return Period ≥ ${myLevel.lowerBound} Years`;
                }
            };
        },
        forecastLevelByNumber() {
            return (riskLevel) => {
                return this.forecastLevels.find(el => el.riskLevel === riskLevel);
            }
        },
        gaugeLevelByNumber() {
            return (riskLevel) => {
                return this.gaugeLevels.find(el => el.riskLevel === riskLevel);
            }
        },
    },
    actions: {
        async initializeSettingStore() {
            try {
                const [riskLevelResponse, settingResponse, overlayResponse] = await Promise.all([
                    getBaseOrganizationSettings(), getBaseSettings(), getOverlayGeoJSON()
                ]);

                this.forecastLevels = riskLevelResponse.locationsData;
                this.gaugeLevels = riskLevelResponse.gaugesData;
                if (riskLevelResponse.activeSubscription.isActive) {
                    this.assetCount = riskLevelResponse.activeSubscription.assetCount;
                    this.hasActiveSubscription = true;
                } else {
                    this.hasActiveSubscription = false;
                    throw new Error("User does not have Active Subscriptions");
                }

                this.unitPreference = settingResponse.unitPreference;
                this.timezone = settingResponse.timezone;
                this.userTzAbbr = settingResponse.userTzAbbr;
                this.showRainScale = settingResponse.showRainScale;
                this.showGaugeScale = settingResponse.showGaugeScale;
                this.forecastNotificationSettings = settingResponse.forecastNotificationSettings;
                this.gaugeNotificationSettings = settingResponse.gaugeNotificationSettings;
                this.gaugeNotificationSettings.riskLevelData =
                this.gaugeNotificationSettings.riskLevelData.sort((a, b) => a.riskLevel < b.riskLevel ? -1 : 1);
                this.email = settingResponse.email;
                this.alertByEmail = settingResponse.alertByEmail;
                this.phoneNo = settingResponse.phoneNo;
                this.alertBySms = settingResponse.alertBySms;
                this.isAdmin = settingResponse.isAdmin;

                if (overlayResponse.length) {
                    this.overlays = overlayResponse;
                }

                this.loaded = true;

                // if (env.VITE_APP_ENVIRONMENT === "Production") {
                //     // eslint-disable-next-line
                //     pendo.initialize({
                //         visitor: {
                //             id: settingResponse.userId,
                //         },
                //         account: {
                //             id: settingResponse.userId,
                //             isAdmin: settingResponse.isAdmin,
                //         },
                //     });
                //     // eslint-disable-next-line
                //     pendo.identify({
                //         visitor: {
                //             id: settingResponse.userId,
                //         },
                //         account: {
                //             id: settingResponse.userId,
                //             isAdmin: settingResponse.isAdmin,
                //         },
                //     });
                //     // eslint-disable-next-line
                //     pendo.validateEnvironment();
                // }
            } catch (e) {
                console.error(e);
            }
        },
        getForecastSeverityColor(severity) {
            const myLevel = this.forecastLevels.find(
                (level) => level.riskLevel === severity,
            );
            if (!myLevel) return "#656565";
            return myLevel.color;
        },

        getGaugeSeverityColor(severity) {
            if (severity < 0) return "#d9d9d9";
            const myLevel = this.gaugeLevels.find(
                (level) => level.riskLevel === severity,
            );
            if (!myLevel) return "rgb(156, 194, 255)";
            return myLevel.color;
        },
        clear() {
            this.$reset();
        },
    },
});
